import { useId } from "react";

export default function GenderGenderfluidIconMini({
  size = 28,
  active = false,
  title = "Genderfluid",
  ...rest
}) {
  const uid = useId();
  const id = (name) => `${uid}-${name}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={title}
      style={{ display: "block" }}
      data-preserve
      {...rest}
    >
      <title>{title}</title>

      <defs>
        {/* Outer glow – genderfluid palette */}
        <radialGradient id={id("rimGlow")} cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="#12001a" stopOpacity="0" />
          <stop offset="80%" stopColor="#FF6DA8" stopOpacity="0.55" />
          <stop offset="95%" stopColor="#B476FF" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#4BB3FF" stopOpacity="0.75" />
        </radialGradient>

        {/* Rim stroke: pink → white → purple → blue */}
        <linearGradient
          id={id("rimStroke")}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#FF6DA8" />
          <stop offset="30%" stopColor="#FFFFFF" />
          <stop offset="65%" stopColor="#B476FF" />
          <stop offset="100%" stopColor="#4BB3FF" />
        </linearGradient>

        {/* Inner face fill */}
        <radialGradient id={id("faceFill")} cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#1b1024" />
          <stop offset="70%" stopColor="#090411" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>

        {/* Soft highlight on upper rim */}
        <radialGradient id={id("rimHighlight")} cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#ffffff" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>

        {/* Symbol stroke – same palette */}
        <linearGradient
          id={id("symbolStroke")}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#FF6DA8" />
          <stop offset="30%" stopColor="#FFFFFF" />
          <stop offset="65%" stopColor="#B476FF" />
          <stop offset="100%" stopColor="#4BB3FF" />
        </linearGradient>

        {/* Glow for the symbol */}
        <filter
          id={id("symbolGlow")}
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="b1" />
          <feColorMatrix
            in="b1"
            type="matrix"
            values="
              1 0 0 0 0.9
              0 1 0 0 0.7
              0 0 1 0 0.9
              0 0 0 1 0
            "
          />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Active aura */}
        <filter
          id={id("activeAura")}
          x="-80%"
          y="-80%"
          width="260%"
          height="260%"
        >
          <feGaussianBlur stdDeviation="6" result="b1" />
          <feMerge>
            <feMergeNode in="b1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Tiny spark glows */}
        <filter
          id={id("spark")}
          x="-200%"
          y="-200%"
          width="400%"
          height="400%"
        >
          <feGaussianBlur stdDeviation="0.6" />
        </filter>
      </defs>

      {/* ===== Badge base ===== */}
      <g filter={active ? `url(#${id("activeAura")})` : undefined}>
        <circle cx="32" cy="32" r="30" fill={`url(#${id("rimGlow")})`} />
        <circle
          cx="32"
          cy="32"
          r="30"
          fill="none"
          stroke={`url(#${id("rimStroke")})`}
          strokeWidth="4"
        />
        <circle
          cx="32"
          cy="24"
          r="26"
          fill={`url(#${id("rimHighlight")})`}
          opacity="0.4"
        />
        <circle cx="32" cy="32" r="24" fill={`url(#${id("faceFill")})`} />
        <circle
          cx="32"
          cy="32"
          r="24"
          fill="none"
          stroke="#ffffff"
          strokeOpacity="0.16"
          strokeWidth="1.25"
        />
      </g>

      {/* ===== Genderfluid symbol – circle + flowing wave + small arrow ===== */}
      <g
        filter={`url(#${id("symbolGlow")})`}
        stroke={`url(#${id("symbolStroke")})`}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {/* Circle base */}
        <circle cx="30" cy="33" r="7" />

        {/* Fluid wave inside the circle */}
        <path
          d="
            M 24 34
            Q 27 30 30 34
            T 36 34
          "
          strokeWidth="2.5"
        />

        {/* Small diagonal arrow at bottom-right: hint that identity can move/flow */}
        <line x1="33.5" y1="38" x2="38" y2="42" strokeWidth="2.4" />
        <line x1="36" y1="42" x2="38" y2="42" strokeWidth="2.4" />
        <line x1="38" y1="40" x2="38" y2="42" strokeWidth="2.4" />
      </g>

      {/* ===== Twinkling sparks ===== */}
      <g opacity="0.9">
        <circle
          cx="20"
          cy="22"
          r="0.8"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="44"
          cy="18"
          r="0.6"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="48"
          cy="36"
          r="0.7"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="18"
          cy="40"
          r="0.5"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
      </g>
    </svg>
  );
}
